import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';
import { ChatContactsResponse, ChatAiCompanionDto } from '../types/auth';

/**
 * CORE-607 — the chat contacts list (people + AI companions) split out of
 * StreamChatContext so it no longer requires a GetStream connection.
 *
 * Contacts and AI companions are pure backend data (`api.getChatContacts` /
 * `api.getAiCompanions`, DB-backed) — they never depended on Stream to be
 * fetched; they merely lived inside the Stream provider and loaded only *after*
 * the Stream socket connected. That coupling caused the CORE-119 RU regression
 * where a hung post-connect GET held the whole chat screen on a loader.
 *
 * Here they load on authentication alone, independent of any chat backend, so
 * the list is available whether a surface is on Stream or Agape.ChatApi. This
 * provider is mounted ABOVE StreamChatProvider so the latter can read
 * `aiCompanions` for the Wisdom Council init.
 */
interface ChatContactsContextType {
  contacts: ChatContactsResponse | null;
  aiCompanions: ChatAiCompanionDto[];
  /**
   * CORE-1528 — контакты запрошены, и запрос не ответил (`contacts` остался
   * null ПОСЛЕ завершённой загрузки). По голому `null` «ещё грузится» и «не
   * загрузилось» неотличимы, и нативный список на отказе крутил спиннер
   * вечно — из РФ первый `/api/chat/contacts` может сорваться. Сбрасывается
   * на время повторной загрузки и при выходе. Веб это поле не читает.
   */
  contactsFailed: boolean;
  /** Re-fetch contacts + AI companions (e.g. after adding a friend / master peer). */
  refreshContacts: () => Promise<void>;
}

const ChatContactsCtx = createContext<ChatContactsContextType | null>(null);

export const ChatContactsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [contacts, setContacts] = useState<ChatContactsResponse | null>(null);
  const [aiCompanions, setAiCompanions] = useState<ChatAiCompanionDto[]>([]);
  const [contactsFailed, setContactsFailed] = useState(false);

  const load = useCallback(async () => {
    // Повтор после отказа снова показывает загрузку, а не прежний отказ.
    setContactsFailed(false);
    // Both calls are individually best-effort so one failing (e.g. AI users not
    // provisioned on this backend) never blanks the whole list.
    const [contactsData, aiData] = await Promise.all([
      api.getChatContacts().catch(() => null),
      api.getAiCompanions().catch(() => []),
    ]);
    setContacts(contactsData);
    setAiCompanions(aiData);
    setContactsFailed(contactsData === null);
  }, []);

  // Load on auth; clear on logout. No Stream connection required.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setContacts(null);
      setAiCompanions([]);
      setContactsFailed(false);
      return;
    }
    setContactsFailed(false);
    let cancelled = false;
    (async () => {
      try {
        const [contactsData, aiData] = await Promise.all([
          api.getChatContacts().catch(() => null),
          api.getAiCompanions().catch(() => []),
        ]);
        if (!cancelled) {
          setContacts(contactsData);
          setAiCompanions(aiData);
          setContactsFailed(contactsData === null);
        }
      } catch {
        /* non-fatal — surfaces render with an empty list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const refreshContacts = useCallback(async () => {
    try {
      await load();
    } catch {
      /* non-fatal */
    }
  }, [load]);

  return (
    <ChatContactsCtx.Provider value={{ contacts, aiCompanions, contactsFailed, refreshContacts }}>
      {children}
    </ChatContactsCtx.Provider>
  );
};

export const useChatContacts = (): ChatContactsContextType => {
  const context = useContext(ChatContactsCtx);
  if (!context) {
    throw new Error('useChatContacts must be used within a ChatContactsProvider');
  }
  return context;
};

/** Safe variant that returns null outside the provider (mirrors useStreamChatSafe). */
export const useChatContactsSafe = (): ChatContactsContextType | null => {
  return useContext(ChatContactsCtx);
};
